import _handleApiCall from '../../../helper/handleApiCall';
import apiManager from '../../../utils/apiManager';
import ApiClientModel, {
  ApiClientResponseModel,
} from '../../BankIntegration/models/api_client.model';
import MurabahaApis from '../configs/murabaha.apis';
import InvestorBankDetails from '../models/bank_details.model';
import IBANVerificationResult from '../models/ibanVerification';
import InvestorDto from '../models/investor.dto';
import InvestorModel from '../models/murabaha_investor';
import { ApiResponse } from './murabaha.type';

class MurabahaInvestorServices {
  /** Save the investor into the db */
  static async createInvestor<T>(investorDto: InvestorDto): Promise<ApiResponse<T>> {
    let result: ApiResponse<T> = { isSuccess: false };
    try {
      const res = await apiManager.post(
        MurabahaApis.CREATE_INVESTOR,
        investorDto.toJson()
      );

      if (res.status === 201 && res.data.data.id) {
        result = {
          data: res.data.data.id,
          isSuccess: true,
        };
      }
    } catch (error) {
      result = {
        isSuccess: false,
        message: error?.response?.data?.message,
        data: error?.response?.data,
      };
    }

    return result;
  }

  /** Get the Investor by the ID */
  static async getInvestorById(
    investorId: string
  ): Promise<ApiResponse<InvestorModel>> {
    let result: ApiResponse<InvestorModel> = { isSuccess: false };
    try {
      const res = await apiManager.get(
        `${MurabahaApis.GET_INVESTOR_BY_ID}/?id=${investorId}`
      );

      if (res.status === 200 && res.data.results.length > 0) {
        result = {
          data: InvestorModel.fromJson(res.data.results[0] as any),
          isSuccess: true,
        };
      }
    } catch (error) {
      result = {
        isSuccess: false,
        message: error?.response?.data?.message,
      };
    }

    return result;
  }

  /** Verify the IBAN of the USER */
  static async verifyIban(investorDto: InvestorDto) {
    let result: ApiResponse<IBANVerificationResult> = { isSuccess: false };
    try {
      const res = await apiManager.post(MurabahaApis.VERIFY_IBAN_PROXY, {
        iban: investorDto.iban,
        national_id: investorDto.national_id,
      });

      if (res.status === 200 && res.data && res.data.result) {
        result = {
          data: IBANVerificationResult.fromJson(res.data.result),
          isSuccess: true,
        };
      }
    } catch (error) {
      result.message = error?.response?.data?.error;
    }

    return result;
  }

  /** Get the bank details of the investor */
  static async getInvestorBankDetails(userId: number) {
    const res = await _handleApiCall(async () => {
      return apiManager.get(MurabahaApis.MURABAHA_INVESTOR_BANK_DETAILS(userId));
    });
    if (res.isSuccess && res.data) {
      return InvestorBankDetails.fromJson(res.data.data);
    } else {
      return undefined;
    }
  }

  /** Extract the API clients from data */
  static extractApiClients(data: any) {
    if (data && data.results) {
      let clients = Object.values(data.results).map((d) =>
        ApiClientModel.fromJson(d)
      );
      return {
        count: data.count,
        clients,
      };
    } else {
      return {
        count: 0,
        clients: [],
      };
    }
  }

  /** Extract the API clients from data */
  static extractMurabahaClients(data: any): InvestorModel[] {
    if (data && data.results) {
      let clients = Object.values(data.results).map((d) =>
        InvestorModel.fromJson(d)
      );
      return clients;
    } else {
      return [];
    }
  }

  static async createApiClient(apiClient: ApiClientModel) {
    const formData = new FormData();
    formData.append('name', apiClient.name);
    formData.append('created_by', apiClient.created_by.toString());
    formData.append('ip_whitelist', JSON.stringify(apiClient.ip_whitelist));
    formData.append('x509_certificate', apiClient.x509_certificate);

    const res = await _handleApiCall(async () => {
      return apiManager.post(MurabahaApis.MURABAHA_API_CLIENTS, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    });

    if (res.isSuccess) {
      res.data = ApiClientResponseModel.fromJson(res.data);
    } else {
      res.data = undefined;
    }

    return res as ApiResponse<ApiClientResponseModel>;
  }

  /** Enable or Disable the client */
  static async blockApiClient(clientId: number) {
    const res = await _handleApiCall(async () => {
      return apiManager.patch(`${MurabahaApis.MURABAHA_API_CLIENTS}${clientId}/`);
    });

    return res;
  }
}

export default MurabahaInvestorServices;
